import { MouseEvent, useMemo, useState } from "react";
import { useParams } from "react-router";
import classNames from "classnames";

import { SearchIcon } from "./assets/SearchIcon";

import { useGetMetadata } from "../../data/metadata/getMetadata/useGetMetadata";

import { ImageModal } from "../../shared/components/ImageModal";
import { Checkbox } from "../../shared/components/Checkbox";

import { useDownloadFiles } from "../../data/files/downloadFiles/useDownloadFiles";

import { Asset } from "../../data/metadata/getMetadata/types";

export function GameAssets() {
  const { gameId } = useParams();
  const { data } = useGetMetadata();
  const [activeAsset, setActiveAsset] = useState<undefined | (Omit<Asset, "size"> & { name: string; size: string })>();
  const [searchTerms, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"alphabetical-asc" | "alphabetical-desc">("alphabetical-asc");
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [checkedImages, setCheckedImages] = useState(new Set<string>());
  const { mutate: downloadFiles, isPending } = useDownloadFiles();

  const assets = useMemo(() => {
    if (!gameId || !data?.categories[gameId]) {
      return [];
    }

    return data.categories[gameId].map((asset) => ({
      ...asset,
      name: asset.path.split("/").pop()!,
      size: `${(asset.size / 1024).toFixed(2)} KB`,
    }));
  }, [data, gameId]);

  const filteredAssets = useMemo(() => {
    let filteredAssets = assets;

    if (searchTerms.trim()) {
      filteredAssets = filteredAssets.filter((asset) => {
        let matches = asset.name.toLowerCase().includes(searchTerms.toLowerCase());
        if (!matches) {
          matches = asset.tags.some((tag) => tag.toLowerCase().includes(searchTerms.toLowerCase()));
        }

        return matches;
      });
    }
    if (selectedTag) {
      filteredAssets = filteredAssets.filter((asset) => asset.tags.includes(selectedTag));
    }
    filteredAssets = filteredAssets.slice().sort((a, b) => {
      if (sortBy === "alphabetical-asc") {
        return a.name.localeCompare(b.name);
      }

      return b.name.localeCompare(a.name);
    });

    return filteredAssets;
  }, [assets, searchTerms, selectedTag, sortBy]);

  const tags = useMemo(() => {
    return [...new Set(assets.map((asset) => asset.tags).flat())];
  }, [assets]);

  const onCheckboxClick = (e: MouseEvent<HTMLInputElement>, asset: (typeof assets)[number]) => {
    e.stopPropagation();
    if (checkedImages.has(asset.id)) {
      checkedImages.delete(asset.id);
    } else {
      checkedImages.add(asset.id);
    }
    setCheckedImages(new Set(checkedImages));
  };

  return (
    <>
      {/*
        Starting from React 19, we could add Document Metadata directly in a component,
        and it will be hoisted into the head tag
      */}
      <link rel="canonical" href={`https://mediakit.blast.tv/assets/${gameId}`} />
      <main className="mx-auto box-content max-w-8xl px-4 py-6 md:px-6">
        {activeAsset && <ImageModal asset={activeAsset} onClose={() => setActiveAsset(undefined)} />}
        <div className="relative mx-auto mb-6 w-full max-w-2xl">
          <input
            type="text"
            placeholder="Search assets"
            className="box-border w-full rounded-full border border-yellow bg-canvas-100 py-2 pl-5 pr-16 font-style-b2-body-copy placeholder:text-white focus:border-yellow focus:placeholder:text-neutral-50 disabled:border-neutral-50"
            value={searchTerms}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            type="button"
            className="absolute right-0 top-1/2 z-10 flex h-full w-12 -translate-y-1/2 items-center justify-center pr-2"
            aria-label="search"
          >
            <SearchIcon />
          </button>
        </div>
        <div className="box-content flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-style-desktop-h3">Assets</h3>
              <div className="rounded-full bg-neutral-50 px-2 py-1 text-canvas-100 font-style-b4-body-copy">
                {assets.length}
              </div>
            </div>
            {tags.length > 0 && (
              <div>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="rounded-full border border-yellow bg-canvas-100 px-2 py-1 font-style-b1-body-copy"
                >
                  <option value="">Asset Tag</option>
                  {tags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-center gap-2 font-style-b1-body-copy">
              <span>Sort By</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "alphabetical-asc" | "alphabetical-desc")}
                className="rounded-full border border-yellow bg-canvas-100 px-2 py-1 font-style-b1-body-copy"
              >
                <option value="alphabetical-asc">Title A-Z</option>
                <option value="alphabetical-desc">Title Z-A</option>
              </select>
            </div>
          </div>
          <div
            className="grid gap-5"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(17rem, 1fr))`,
            }}
          >
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => setActiveAsset(asset)}
                className="group relative h-[17rem] cursor-pointer overflow-hidden rounded bg-canvas-95"
              >
                <Checkbox
                  className={classNames(
                    "absolute left-4 top-4 group-hover:block",
                    checkedImages.has(asset.id) ? "block" : "hidden",
                  )}
                  onClick={(e) => onCheckboxClick(e, asset)}
                  checked={checkedImages.has(asset.id)}
                />
                <img
                  className="size-full object-contain object-center"
                  src={`/${asset.path.split("/")[1]}/preview-${asset.path.split("/")[2]}`}
                  alt=""
                  loading="lazy"
                />
                <div className="absolute -bottom-full left-0 z-10 flex h-20 w-full flex-col items-center justify-center bg-canvas-90 transition-all duration-300 group-hover:bottom-0">
                  <div>{asset.name.split(".")[0]}</div>
                  <div className="flex items-center gap-2">
                    <div className="uppercase font-style-b4-body-copy">{asset.name.split(".").pop()}</div>
                    <div className="font-style-b4-body-copy">{asset.size}</div>
                    {asset.height && asset.width && (
                      <div className="font-style-b4-body-copy">
                        {asset.width}x{asset.height}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {checkedImages.size > 0 && (
          <div className="fixed bottom-5 left-1/2 z-10 flex h-20 w-[75rem] max-w-full -translate-x-1/2 items-center justify-between rounded bg-canvas-90 px-4">
            <span className="font-style-subheading-4">{checkedImages.size} asset(s) to download</span>
            <button
              type="button"
              className="button h-4"
              disabled={isPending}
              onClick={() => {
                const assetsToDownload = assets.filter((asset) => checkedImages.has(asset.id));
                downloadFiles(
                  assetsToDownload.map((asset) => ({
                    url: asset.path,
                    name: asset.name,
                  })),
                );
              }}
            >
              Download
            </button>
          </div>
        )}
      </main>
    </>
  );
}
